import { KeyboardArrowLeft, KeyboardArrowRight, Delete } from "@mui/icons-material";
import { Box, IconButton, Skeleton, Tooltip, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  picturePaths: string[] | null;
  handleDelete?: (picturePathToDelete: string) => void;
  isLoading?: boolean;
  imageSize?: number;
  boxSize?: number;
}

const overlayButtonStyle = {
  position: "absolute",
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  color: "white",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.8)"
  },
  boxShadow: 2,
  zIndex: 2
};

/**
 * Helper function to preload a single image.
 * This is extracted to keep the nesting level low in accordance with SonarQube rules.
 */
export const preloadImage = (src: string): Promise<void> => {
  return new Promise(resolve => {
    if (!src) {
      resolve();
      return;
    }
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => resolve();
    img.src = src;
  });
};

export function EnlargedImageCarousel({
  picturePaths,
  handleDelete,
  isLoading,
  imageSize = 45,
  boxSize = 60
}: Readonly<Props>) {
  const { t } = useTranslation();

  const [allImagesReady, setAllImagesReady] = useState(false);
  const [pictureIndex, setPictureIndex] = useState(0);

  const handleNextButton = () => {
    if (picturePaths && pictureIndex + 1 > picturePaths.length - 1) setPictureIndex(0);
    else setPictureIndex(prevIndex => prevIndex + 1);
  };

  const handleBackButton = () => {
    if (picturePaths && pictureIndex - 1 < 0) setPictureIndex(picturePaths.length - 1);
    else setPictureIndex(prevIndex => prevIndex - 1);
  };

  /**
   * When `isLoading` is true, defer interpreting `picturePaths` (empty may mean "not fetched yet").
   * `null` means paths are not set yet — stay not-ready (skeleton) until an array is passed.
   * `[]` when not loading means confirmed no images; non-empty arrays run preload then `allImagesReady`.
   */
  useEffect(() => {
    if (isLoading) {
      setPictureIndex(0);
      setAllImagesReady(false);
      return;
    }

    if (picturePaths === null) {
      setAllImagesReady(false);
      return;
    }

    if (picturePaths.length === 0) {
      setAllImagesReady(true);
      return;
    }

    setPictureIndex(prev =>
      prev >= picturePaths.length ? Math.max(0, picturePaths.length - 1) : prev
    );

    setAllImagesReady(false);

    let isSubscribed = true;
    const promises = picturePaths.map(preloadImage);

    Promise.all(promises).then(() => {
      if (isSubscribed) {
        setAllImagesReady(true);
      }
    });
    return () => {
      isSubscribed = false;
    };
  }, [picturePaths, isLoading]);

  const isAssetLoading = !allImagesReady;

  /** Same outer size for loading skeleton, empty state, and carousel below. */
  const carouselShellSx = {
    width: "100%",
    height: `${boxSize}vh`,
    minHeight: `${imageSize}vh`,
    borderRadius: 1,
    overflow: "hidden",
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  };

  // Case 1: Loading state (either global loading, preload not done, or paths not provided yet)
  if (isLoading || isAssetLoading || picturePaths === null) {
    return (
      <Box data-testid="enlarged-image-carousel-loading" sx={carouselShellSx}>
        <Skeleton variant="rectangular" width="100%" height="100%" animation="wave" />
      </Box>
    );
  }

  // Case 2: Confirmed no images (empty array; `null` is handled in Case 1)
  if (picturePaths.length === 0)
    return (
      <Box
        data-testid="enlarged-image-carousel-no-images"
        sx={{
          ...carouselShellSx,
          backgroundColor: "rgba(0, 0, 0, 0.05)"
        }}
      >
        <Typography variant="subtitle1" color="textSecondary">
          {t("No images available")}
        </Typography>
      </Box>
    );

  // Case 3: Carousel with overlay buttons
  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        maxWidth: "100%",
        height: `${boxSize}vh`,
        minHeight: `${imageSize}vh`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}
    >
      {/* Image */}
      <Box
        component="img"
        src={picturePaths[pictureIndex]}
        alt="enlarged-image"
        data-testid="enlarged-image"
        sx={{
          maxWidth: "100%",
          maxHeight: "100%",
          minHeight: `${imageSize}vh`,
          objectFit: "contain",
          borderRadius: 1
        }}
      />

      {/* Delete */}
      {handleDelete && (
        <Tooltip title={t("Delete")}>
          <IconButton
            data-testid="delete-image-button"
            onClick={() => handleDelete(picturePaths[pictureIndex])}
            sx={{ ...overlayButtonStyle, top: 8, right: 8 }}
          >
            <Delete />
          </IconButton>
        </Tooltip>
      )}

      {/* Navigation */}
      {picturePaths.length > 1 && (
        <>
          <IconButton
            onClick={handleBackButton}
            data-testid="previous-image-button"
            sx={{
              ...overlayButtonStyle,
              left: 8,
              top: "50%",
              transform: "translateY(-50%)"
            }}
          >
            <KeyboardArrowLeft />
          </IconButton>

          <IconButton
            onClick={handleNextButton}
            data-testid="next-image-button"
            sx={{
              ...overlayButtonStyle,
              right: 8,
              top: "50%",
              transform: "translateY(-50%)"
            }}
          >
            <KeyboardArrowRight />
          </IconButton>

          <Typography
            variant="body2"
            sx={{
              position: "absolute",
              bottom: 8,
              left: "50%",
              transform: "translateX(-50%)",
              backgroundColor: "rgba(0,0,0,0.5)",
              color: "white",
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              fontWeight: 600,
              pointerEvents: "none"
            }}
          >
            {pictureIndex + 1} / {picturePaths.length}
          </Typography>
        </>
      )}
    </Box>
  );
}
